import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { Media } from '../ui/Media';
import { clsBouton, Accordéon, Note, Entete } from './partages';
import { ProductCard } from '../components/ProductCard';
import { Segmente } from '../ui/kit';
import { AVIS, BOUTIQUES, ETAPES, QUESTIONS, REGLAGES, VALEURS } from '../data/mock-data';
import {
  dateFr, eur, eurSansCentimes, familles, kg, produitsSignature, produitsDe, prixPour, poidsParDefaut
} from '../data/repository';

/**
 * ACCUEIL — la vitrine comme une entrée de boutique.
 *
 * Rythme voulu : une promesse, une preuve, le comptoir, la méthode, l'histoire,
 * les avis. Chaque bloc a **une** idée et **une** action. Aucun cadre autour des
 * sections : la séparation se fait par l'air et la teinte de fond.
 */

/* Les six fonds de famille viennent des jetons (`--teinte-*`), jamais d'un hexadécimal
   écrit ici : une teinte se règle au même endroit que le reste de la palette. */
const FONDS: Record<string, string> = {
  wine: 'from-tinte-wine-100 to-tinte-wine-200',
  blush: 'from-tinte-blush-100 to-tinte-blush-200',
  sand: 'from-tinte-sand-100 to-tinte-sand-200',
  clay: 'from-tinte-clay-100 to-tinte-clay-200',
  olive: 'from-tinte-olive-100 to-tinte-olive-200',
  slate: 'from-tinte-slate-100 to-tinte-slate-200',
};

export function Accueil() {
  const [parcours, setParcours] = useState<'RETRAIT' | 'LIVRAISON'>('RETRAIT');
  const signature = produitsSignature(4);
  const cote = produitsDe('boeuf').find(p => p.id === 'p_cote_boeuf_28');
  const agneau = produitsDe('mechoui-receptions').find(p => p.id === 'p_agneau_entier_mechoui');

  return (
    <main id="contenu">
      {/* ============================================================ HÉRO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-brass-wash blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-[1280px] items-center gap-10 px-5 pb-14 pt-8 sm:px-6 lg:grid-cols-[1.02fr_1fr] lg:gap-16 lg:pb-24 lg:pt-16">
          <div>
            <p className="eyebrow flex items-center gap-2">
              <span className="inline-block h-px w-8 bg-laiton-500" aria-hidden="true" />
              Boucherie de famille · Val-d’Oise
            </p>
            <h1 className="mt-4 text-balance text-[clamp(2.4rem,6vw,4.25rem)] leading-[0.98] text-ink">
              Découpé pour vous,
              <br />
              <span className="italic text-wine">à la commande.</span>
            </h1>
            <p className="mt-5 max-w-xl text-[1.0625rem] leading-relaxed text-ink-soft sm:text-lg">
              Côte de bœuf maturée vingt-huit jours, agneau de nos éleveurs, volailles abattues
              à Sannois. Vous choisissez la pièce et le poids — nous découpons le matin même.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/boutique" className={clsBouton('primary', 'btn--lg')}>
                Composer ma commande <Icon name="arrowRight" size={18} />
              </Link>
              <a href="#etapes" className={clsBouton('outline', 'btn--lg')}>Comment ça marche</a>
            </div>

            <dl className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3">
              <div className="flex items-center gap-2">
                <Note note={REGLAGES.marque.note} avis={REGLAGES.marque.avis} compact />
              </div>
              <div className="flex items-center gap-2 text-sm text-ink-soft">
                <Icon name="store" size={17} className="text-wine" />
                Retrait sous {REGLAGES.creneaux.delaiDecoupeH} h
              </div>
              <div className="flex items-center gap-2 text-sm text-ink-soft">
                <Icon name="truck" size={18} className="text-wine" />
                Livraison {REGLAGES.livraison.delaiMinH}–{REGLAGES.livraison.delaiMaxH} h
              </div>
            </dl>
          </div>

          {/* Visuel principal : la pièce du dimanche, avec une carte posée dessus. */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-[28px] shadow-lift">
              <Media src={cote?.photo} alt="Côte de bœuf maturée sur le billot de la Maison Duval"
                teinte="wine" ratio="aspect-[4/5] lg:aspect-[5/6]" monogramme="D" priority />
              <div className="absolute inset-0 bg-linear-to-t from-ink/45 via-transparent to-transparent" aria-hidden="true" />
            </div>

            {cote && (
              <div className="absolute inset-x-4 bottom-4 rounded-[20px] bg-paper/96 p-4 shadow-float backdrop-blur sm:inset-x-6 sm:bottom-6 sm:p-5">
                <p className="eyebrow text-brass">La pièce du dimanche</p>
                <div className="mt-1.5 flex items-end justify-between gap-4">
                  <div>
                    <p className="font-display text-xl leading-tight sm:text-2xl">{cote.nom}</p>
                    <p className="mt-1 text-sm text-muted">
                      {eur(cote.prixKg!)}/kg · {kg(poidsParDefaut(cote))} ≈ {eur(prixPour(cote, poidsParDefaut(cote)))}
                    </p>
                  </div>
                  <Link to={`/produit/${cote.slug}`}
                    className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-ink text-lin-50 transition hover:bg-wine"
                    aria-label={`Voir ${cote.nom}`}>
                    <Icon name="arrowRight" size={20} />
                  </Link>
                </div>
              </div>
            )}

            <div className="absolute -right-2 top-6 hidden rotate-3 rounded-2xl bg-ink px-3.5 py-2 text-center text-lin-50 shadow-float sm:block">
              <p className="text-[0.625rem] uppercase tracking-[0.16em] text-brass-soft">Pesée</p>
              <p className="font-display text-lg leading-none">±{REGLAGES.poids.tolerancePct} %</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================== BANDEAU CONFIANCE */}
      <section aria-label="Nos engagements" className="border-y border-linen-deep/70 bg-linen/60">
        <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-6 px-5 py-7 sm:px-6 lg:grid-cols-4">
          {[
            { icone: 'knife' as const, titre: 'Découpe à la commande', texte: 'Le matin même, par le boucher' },
            { icone: 'scale' as const, titre: `Tolérance ±${REGLAGES.poids.tolerancePct} %`, texte: 'La maison absorbe l’écart' },
            { icone: 'snow' as const, titre: 'Chaîne du froid 0–4 °C', texte: 'Colis isotherme Chronofresh' },
            { icone: 'leaf' as const, titre: 'Éleveurs de confiance', texte: 'Choisis sur pied, en France' },
          ].map(e => (
            <div key={e.titre} className="flex items-start gap-3">
              <Icon name={e.icone} size={20} className="mt-0.5 shrink-0 text-wine" />
              <div>
                <p className="text-[0.8125rem] font-semibold leading-tight text-ink sm:text-sm">{e.titre}</p>
                <p className="mt-0.5 text-[0.75rem] leading-snug text-muted sm:text-[0.8125rem]">{e.texte}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Familles />

      {/* ======================================================== SIGNATURES */}
      <section className="bg-linen/70 py-16 lg:py-24">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-6">
          <Entete titre="Les pièces qu’on nous demande" surtitre="Le choix du boucher"
            texte="Quatre classiques de la maison, à commander toute la semaine."
            lien={{ vers: '/boutique', libelle: 'Tout voir' }} />
          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 lg:grid-cols-4">
            {signature.map((p, i) => <ProductCard key={p.id} produit={p} prioritaire={i < 2} />)}
          </div>
        </div>
      </section>

      {/* =========================================================== ÉTAPES */}
      <section id="etapes" className="mx-auto max-w-[1280px] scroll-mt-28 px-5 py-16 sm:px-6 lg:py-24">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <Entete titre="De votre panier au billot" surtitre="Comment ça marche"
            texte="Quatre étapes, aucune surprise : le poids, le prix et l’heure sont annoncés avant que vous ne payiez." />
          <div className="shrink-0">
            <Segmente libelle="Parcours" valeur={parcours}
              onChange={v => setParcours(v as 'RETRAIT' | 'LIVRAISON')}
              options={[
                { valeur: 'RETRAIT', libelle: 'Retrait', icone: 'store' },
                { valeur: 'LIVRAISON', libelle: 'Livraison', icone: 'truck' },
              ]} />
          </div>
        </div>

        <ol className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {(parcours === 'RETRAIT' ? ETAPES : [
            { cle: 'commande', titre: 'Vous commandez avant midi',
              texte: `Commande passée avant ${REGLAGES.livraison.coupure} : le colis part le jour ouvré suivant, du mardi au samedi.` },
            { cle: 'colis', titre: 'Nous préparons le colis',
              texte: `Découpe du matin, colis isotherme maintenu ${REGLAGES.livraison.temperature}, expédié en ${REGLAGES.livraison.transporteur}.` },
            { cle: 'transport', titre: 'Le transporteur prend le relais',
              texte: `${REGLAGES.livraison.delaiMinH}–${REGLAGES.livraison.delaiMaxH} h ouvrées. Un SMS annonce le jour et le numéro de suivi.` },
            { cle: 'reglement', titre: 'Tout est réglé en ligne',
              texte: `La livraison se règle intégralement à la commande — aucune arrhes à ajouter. Offerte dès ${eurSansCentimes(REGLAGES.livraison.seuilGratuite)}.` },
          ]).map((e, i) => (
            <li key={e.cle} className="relative">
              {i < 3 && (
                <span className="absolute left-[38px] top-12 hidden h-[calc(100%-2rem)] w-px bg-linen-deep lg:block" aria-hidden="true" />
              )}
              <div className="relative z-10 mb-4 grid h-12 w-12 place-items-center rounded-full bg-surface font-display text-lg text-wine shadow-soft">
                {String(i + 1).padStart(2, '0')}
              </div>
              <h3 className="text-lg">{e.titre}</h3>
              <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-muted">{e.texte}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ======================================================== ÉDITORIAL */}
      <section className="bg-ink py-16 text-lin-50 lg:py-24">
        <div className="mx-auto grid max-w-[1280px] items-center gap-10 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16">
          <div className="relative order-2 lg:order-1">
            <div className="overflow-hidden rounded-[24px]">
              <Media src={produitsDe('boeuf')[0]?.photo} alt="Le cabinet de maturation de la Maison Duval"
                teinte="wine" ratio="aspect-[4/3]" monogramme="28" />
            </div>
            <div className="absolute -bottom-5 -right-2 rounded-2xl bg-brass px-5 py-4 text-ink shadow-float sm:right-6">
              <p className="font-display text-3xl leading-none">28</p>
              <p className="mt-1 text-[0.6875rem] font-semibold uppercase tracking-[0.12em]">jours de maturation</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="eyebrow text-brass-soft">Notre maison</p>
            <h2 className="mt-3 text-[clamp(1.9rem,3.4vw,2.9rem)] text-lin-50">
              Le goût du temps,<br />pas celui de la vitesse.
            </h2>
            <p className="mt-5 max-w-lg text-[0.9375rem] leading-relaxed text-lin-50/70">
              Nous choisissons nos animaux sur pied, chez des éleveurs que nous connaissons depuis
              des années. Puis nous les laissons reposer : vingt-huit jours pour un bœuf, trente-cinq
              pour un Angus. Ce temps coûte cher — il donne ce goût de noisette qu’on ne fabrique pas.
            </p>
            <ul className="mt-7 space-y-4">
              {VALEURS.map(v => (
                <li key={v.titre} className="flex gap-3">
                  <Icon name="check" size={18} className="mt-0.5 shrink-0 text-brass-soft" />
                  <p className="text-sm text-lin-50/75"><strong className="font-semibold text-lin-50">{v.titre}.</strong> {v.texte}</p>
                </li>
              ))}
            </ul>
            <Link to="/notre-maison" className={clsBouton('primary', 'mt-8')}>
              Notre histoire depuis 1922 <Icon name="arrowRight" size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* ========================================================== MÉCHOUI */}
      {agneau && (
        <section className="mx-auto max-w-[1280px] px-5 py-16 sm:px-6 lg:py-24">
          <div className="grid overflow-hidden rounded-[28px] bg-linen-deep/40 lg:grid-cols-[1.15fr_1fr]">
            <div className="p-7 sm:p-10 lg:p-14">
              <p className="eyebrow text-wine">Les grandes tables</p>
              <h2 className="mt-3 text-[clamp(1.8rem,3.2vw,2.6rem)]">
                Un méchoui pour trente,<br />sans y penser.
              </h2>
              <p className="mt-4 max-w-md text-[0.9375rem] leading-relaxed text-ink-soft">
                Agneau entier préparé et mariné vingt-quatre heures dans notre atelier, mis en broche
                le jour même. Nous calculons la quantité avec vous, et nous vous rappelons pour le timing.
              </p>
              <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                {[{ k: 'À partir de', v: eurSansCentimes(149) }, { k: 'Convives', v: '8 à 30' }, { k: 'Délai', v: '72 h' }].map(x => (
                  <div key={x.k}>
                    <dt className="text-[0.6875rem] uppercase tracking-[0.12em] text-muted">{x.k}</dt>
                    <dd className="font-display text-xl">{x.v}</dd>
                  </div>
                ))}
              </dl>
              <Link to={`/produit/${agneau.slug}`} className={clsBouton('ink', 'mt-7')}>
                Voir les formules <Icon name="arrowRight" size={17} />
              </Link>
            </div>
            <div className="relative min-h-[240px]">
              <Media src={agneau.photo} alt="Agneau entier à la broche" teinte="sand"
                ratio="h-full min-h-[240px]" monogramme="M" className="absolute inset-0" />
            </div>
          </div>
        </section>
      )}

      {/* ============================================================= AVIS */}
      <section className="bg-linen/70 py-16 lg:py-24">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="eyebrow">Ils reviennent</p>
              <h2 className="mt-2 text-[clamp(1.8rem,3.2vw,2.6rem)]">Ce que le comptoir entend</h2>
            </div>
            <div className="flex items-center gap-3 rounded-full bg-surface px-4 py-2 shadow-soft">
              <Note note={REGLAGES.marque.note} />
              <span className="text-sm text-muted">{REGLAGES.marque.avis} avis vérifiés</span>
            </div>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {AVIS.slice(0, 4).map(a => (
              <figure key={a.id} className="flex h-full flex-col rounded-[20px] bg-surface p-5 shadow-soft">
                <Note note={a.note} compact />
                <blockquote className="mt-3 flex-1">
                  <p className="font-display text-[1.0625rem] leading-snug text-ink">« {a.titre} »</p>
                  <p className="mt-2 text-[0.8125rem] leading-relaxed text-muted">{a.texte}</p>
                </blockquote>
                <figcaption className="mt-4 text-[0.75rem] text-muted">
                  <span className="font-semibold text-ink-soft">{a.auteur}</span> · {a.ville} · {dateFr(a.date)}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================= BOUTIQUES */}
      <section className="mx-auto max-w-[1280px] px-5 py-16 sm:px-6 lg:py-24">
        <Entete titre="Trois comptoirs, un atelier" surtitre="Nous trouver"
          texte="Sannois, Franconville, Argenteuil : la même viande, les mêmes bouchers. Vous retirez dans la boutique que vous choisissez."
          lien={{ vers: '/nos-boutiques', libelle: 'Toutes les adresses' }} />

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {BOUTIQUES.map(b => (
            <article key={b.id} className="flex flex-col rounded-[20px] bg-surface p-5 shadow-soft">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display text-xl">{b.nom}</p>
                  <p className="mt-0.5 text-[0.8125rem] text-muted">{b.responsable} et son équipe</p>
                </div>
                <span className="rounded-full bg-success-wash px-2.5 py-1 text-[0.6875rem] font-semibold text-success">
                  Retrait gratuit
                </span>
              </div>
              <p className="mt-3 text-sm text-ink-soft">{b.adresse}</p>
              <p className="mt-2 flex items-center gap-1.5 text-[0.8125rem] text-muted">
                <Icon name="clock" size={14} />
                {b.horaires.find(h => h.heures !== 'Fermé')?.heures ?? 'Horaires au comptoir'}
              </p>
              <p className="mt-1 text-[0.75rem] text-muted">{b.specialites[0]}</p>
              <div className="mt-auto flex gap-4 pt-4">
                <a href={`tel:${b.telephone.replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-wine underline underline-offset-4">
                  <Icon name="phone" size={14} /> Appeler
                </a>
                <Link to="/nos-boutiques" className="text-sm font-medium text-ink-soft underline underline-offset-4 hover:text-ink">
                  Horaires & accès
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ============================================================== FAQ */}
      <section className="mx-auto max-w-[820px] px-5 py-16 sm:px-6 lg:py-24">
        <p className="eyebrow text-center">Questions fréquentes</p>
        <h2 className="mt-2 text-balance text-center text-[clamp(1.8rem,3.2vw,2.6rem)]">Tout ce qu’on nous demande</h2>
        <div className="mt-8">
          {QUESTIONS.map((q, i) => (
            <Accordéon key={q.question} titre={q.question} ouvertParDefaut={i === 0}>{q.reponse}</Accordéon>
          ))}
        </div>
        <p className="mt-8 text-center text-sm text-muted">
          Une autre question ? <Link to="/nos-boutiques" className="font-semibold text-ink underline underline-offset-4">Appelez le comptoir</Link> — un boucher répond.
        </p>
      </section>
    </main>
  );
}

/** Les dix familles — réutilisées telles quelles par la page 404. */
export function Familles() {
  return (
    <section className="mx-auto max-w-[1280px] px-5 py-12 sm:px-6">
      <Entete titre="Le comptoir, par famille" surtitre="Dix rayons"
        texte="Du bœuf maturé à l’épicerie : tout ce que nous découpons, préparons et choisissons."
        lien={{ vers: '/boutique', libelle: 'Tout le catalogue' }} />

      <div className="-mx-5 mt-8 flex snap-x-mandatory gap-3 overflow-x-auto px-5 pb-2 no-scrollbar sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 lg:grid-cols-5">
        {familles().map(f => (
          <Link key={f.slug} to={`/boutique/${f.slug}`}
            className={`group relative flex min-h-[168px] w-[74%] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-[20px] bg-linear-to-br p-5 ${FONDS[f.teinte] ?? FONDS.sand} sm:min-h-[176px] sm:w-auto`}>
            <div>
              <p className="text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-ink/75">{f.surtitre}</p>
              <p className="mt-1.5 font-display text-[1.35rem] leading-tight text-ink">{f.nom}</p>
            </div>
            <div className="flex items-end justify-between gap-2">
              <p className="text-[0.8125rem] text-ink/70">
                {`${f.nombre} pièce${f.nombre !== 1 ? 's' : ''}`}{f.aPartirDe ? ` · dès ${eurSansCentimes(f.aPartirDe)}` : ''}
              </p>
              <span className="grid h-8 w-8 place-items-center rounded-full bg-ink/10 text-ink transition group-hover:bg-ink group-hover:text-lin-50">
                <Icon name="arrowRight" size={16} />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
